import { useState } from 'react';

/**
 * The tag and link editors, shared by the create and edit screens so the two cannot drift.
 *
 * Both are controlled: the parent owns the list, and these own only the half-typed entry.
 */

/** Add to a list, trimmed, ignoring blanks and duplicates. */
function withItem(list: string[], raw: string): string[] {
  const value = raw.trim();
  if (!value || list.includes(value)) return list;
  return [...list, value];
}

/** The text box and Add button that end both editors. */
function AddRow({
  type,
  placeholder,
  onAdd,
}: {
  type: 'text' | 'url';
  placeholder: string;
  onAdd: (value: string) => void;
}) {
  const [input, setInput] = useState('');
  const add = () => {
    onAdd(input);
    setInput('');
  };
  return (
    <div className="add">
      <input
        type={type}
        value={input}
        placeholder={placeholder}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          // Enter adds the item rather than submitting the whole form.
          if (e.key !== 'Enter') return;
          e.preventDefault();
          add();
        }}
      />
      <button type="button" disabled={!input.trim()} onClick={add}>
        Add
      </button>
    </div>
  );
}

export function TagsField({ tags, onChange }: { tags: string[]; onChange: (t: string[]) => void }) {
  return (
    <div className="field">
      <span className="field-label">Tags</span>
      {tags.length > 0 && (
        <div className="chips">
          {tags.map((tag) => (
            <span className="tag chip" key={tag}>
              {tag}
              <button
                type="button"
                className="remove"
                aria-label={`Remove tag ${tag}`}
                onClick={() => onChange(tags.filter((t) => t !== tag))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <AddRow type="text" placeholder="Add a tag" onAdd={(v) => onChange(withItem(tags, v))} />
    </div>
  );
}

export function LinksField({
  links,
  onChange,
}: {
  links: string[];
  onChange: (l: string[]) => void;
}) {
  return (
    <div className="field">
      <span className="field-label">Links</span>
      {links.length > 0 && (
        <ul className="rows">
          {links.map((href) => (
            <li key={href}>
              <span className="url">{href}</span>
              <button
                type="button"
                className="remove"
                aria-label={`Remove link ${href}`}
                onClick={() => onChange(links.filter((l) => l !== href))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <AddRow type="url" placeholder="Add a link" onAdd={(v) => onChange(withItem(links, v))} />
    </div>
  );
}
